import {
  AirplaneTiltIcon,
  CaretRightIcon,
  HandshakeIcon,
  MotorcycleIcon,
  PauseCircleIcon,
  PencilSimpleIcon,
  PiggyBankIcon,
  PlusIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  TrophyIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'
import { type PlainDate, differenceInDays, formatMoney, percentOf } from '@paybae/shared'
import { useNavigate } from 'react-router'

import { Card, EmptyState, SectionHeader } from '@/components/Card'
import { Donut, type DonutSegment, ProgressRing, chartColor } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import { Money } from '@/components/Money'
import { PersonDot } from '@/components/PersonAvatar'
import { ScreenHeader } from '@/components/ScreenHeader'
import type { FundKind, Member, SavingsGoal } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { personPalette } from '@/lib/persons'
import { useDashboard, useGoals, useHousehold, useRevisiones } from '@/lib/queries'

/**
 * Fondo de ahorro y metas.
 *
 * Una meta sin ritmo es un deseo: saber que faltan $133,000 no dice nada sobre
 * si se va a llegar. Por eso cada meta con fecha muestra **cuánto hay que
 * apartar al mes** para cumplirla, que es la cifra sobre la que sí se puede
 * decidir algo.
 */

const ICONOS: Record<string, typeof PiggyBankIcon> = {
  airplane: AirplaneTiltIcon,
  shield: ShieldCheckIcon,
  motorcycle: MotorcycleIcon,
}

/** Cómo se nombra un fondo sin objetivo en la línea bajo su nombre. */
const ETIQUETA_TIPO: Record<FundKind, string> = {
  GOAL: 'Meta sin monto objetivo',
  EMERGENCY: 'Reserva de emergencia',
  GENERAL: 'Bolsa de ahorro',
  CUSTOM: 'Fondo libre',
}

/**
 * Lo que sustituye al «faltan X» cuando el fondo no tiene meta.
 *
 * Un fondo sin objetivo no está incompleto: está en marcha. El texto lo dice sin
 * dejar el hueco vacío, que es lo que haría pensar que falta un dato.
 */
const SIN_META: Record<FundKind, string> = {
  GOAL: 'Sin monto objetivo: aquí solo se acumula lo que aportes.',
  EMERGENCY: 'Una reserva no se termina, se mantiene. Crece con lo que aportes y no caduca.',
  GENERAL: 'Bolsa sin meta: aquí se junta lo que todavía no tiene destino.',
  CUSTOM: 'Fondo sin meta ni fecha: tú decides cuánto y hasta cuándo.',
}

export function Savings() {
  const metas = useGoals()
  const tablero = useDashboard()
  const hogar = useHousehold()
  const navigate = useNavigate()

  // Las tres consultas se tratan como una sola: sin el tablero no hay total
  // ahorrado y sin el hogar no se sabe de quién es cada meta. Pintar la pantalla
  // a medias mostraría una meta personal como si fuera del fondo común, que es
  // peor que esperar un momento más.
  if (metas.isPending || tablero.isPending || hogar.isPending) return <Cargando />

  const error = metas.error ?? tablero.error ?? hogar.error
  if (error || !metas.data || !tablero.data || !hogar.data) {
    return (
      <>
        {/* El alta no depende de estas consultas: el formulario carga lo suyo.
            Dejar el acceso disponible aunque la lista falle evita que un error
            de red bloquee la única forma de registrar una meta. */}
        <ScreenHeader title="Ahorro" action={<BotonNuevaMeta />} />
        <ErrorState
          titulo="No pudimos cargar tu ahorro"
          mensaje={error instanceof Error ? error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => {
            void metas.refetch()
            void tablero.refetch()
            void hogar.refetch()
          }}
        />
      </>
    )
  }

  const { availability, today: hoy } = tablero.data
  const goals = metas.data
  const integrantes = hogar.data.members

  const totalAhorrado = availability.combined.savingsBalanceCents
  // `combined` no trae `committedSavingsCents`, así que se suma bolsa por bolsa:
  // el fondo común y cada integrante.
  const comprometido =
    (availability.joint?.committedSavingsCents ?? 0) +
    Object.values(availability.perMember).reduce((s, p) => s + p.committedSavingsCents, 0)

  // Reparto del ahorro entre metas: la dona responde «¿a qué está destinado lo
  // que tengo guardado?», que es distinto de «¿cuánto llevo de cada meta?».
  const segments: DonutSegment[] = goals.map((goal, i) => ({
    key: goal.id,
    label: goal.name,
    value: goal.currentAmountCents,
    color: chartColor(i),
  }))
  const repartido = segments.reduce((s, seg) => s + seg.value, 0)

  return (
    <>
      <ScreenHeader
        title="Ahorro"
        subtitle={`${formatMoney(totalAhorrado, { hideZeroCents: true })} guardados`}
        tinte="mint"
        action={<BotonNuevaMeta />}
      />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        {goals.length === 0 ? (
          <Card>
            <EmptyState
              icon={<PiggyBankIcon size={32} weight="fill" />}
              title="Todavía no tienes metas"
              description="Ponle nombre y fecha a lo que estás ahorrando —un viaje, un fondo de emergencia, una moto— y aquí te decimos cuánto apartar cada mes para llegar."
              action={
                <button
                  type="button"
                  onClick={() => {
                    haptic('light')
                    navigate('/ahorro/nueva')
                  }}
                  className="flex min-h-[44px] items-center gap-2 rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
                >
                  <PlusIcon size={16} weight="bold" />
                  Crear mi primera meta
                </button>
              }
            />
          </Card>
        ) : (
          <>
            <Card className="flex flex-col gap-4">
              <SectionHeader title="Destino de tu ahorro" className="mb-0" />
              <div className="flex items-center gap-4">
                <Donut
                  segments={segments}
                  centerValue={formatMoney(repartido, { hideZeroCents: true })}
                  centerLabel="Asignado a metas"
                />
                <ul className="flex min-w-0 flex-1 flex-col gap-2">
                  {segments.map((segment, i) => (
                    <li key={segment.key} className="flex items-baseline gap-2">
                      <span
                        aria-hidden="true"
                        className="size-2 shrink-0 translate-y-[-1px] rounded-full"
                        style={{ background: chartColor(i) }}
                      />
                      <span className="line-clamp-2 text-[13px] leading-tight text-ink-soft">{segment.label}</span>
                      <span data-money className="ml-auto text-[13px] font-semibold text-ink">
                        {formatMoney(segment.value, { hideZeroCents: true })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {comprometido > 0 ? (
                <p className="rounded-card bg-surface-sunken px-3 py-2.5 text-[12px] leading-snug text-ink-soft">
                  Además, <Money cents={comprometido} tone="soft" /> ya decididos este mes que
                  todavía no se traspasan.
                </p>
              ) : null}
            </Card>

            {/* El reparto se decide una vez y se consulta poco, así que vive en
                su propia pantalla y no compite con las fichas de fondo. El
                acceso va aquí, junto a la dona del destino del ahorro, que es
                donde surge la pregunta de a qué se está destinando el ingreso. */}
            <button
              type="button"
              onClick={() => {
                haptic('light')
                navigate('/ahorro/reparto')
              }}
              className="flex min-h-[56px] items-center gap-3 rounded-card border border-line bg-surface px-3.5 py-2.5 text-left active:scale-[0.99]"
            >
              <SlidersHorizontalIcon size={20} className="shrink-0 text-ink-soft" />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-ink">
                  Reparto de tu ingreso
                </span>
                <span className="block text-[12px] leading-snug text-ink-soft">
                  Qué parte de lo que ganas va a cada fondo.
                </span>
              </span>
              <CaretRightIcon size={16} weight="bold" className="shrink-0 text-ink-faint" />
            </button>

            <section className="pb-2">
              <SectionHeader title="Tus fondos" />
              <div className="flex flex-col gap-3">
                {goals.map((goal) => (
                  <MetaFila
                    key={goal.id}
                    goal={goal}
                    hoy={hoy}
                    // Se busca por `id` del integrante, pero al punto de color va
                    // `personColorToken`: son dos cadenas distintas y confundirlas
                    // pintaría a todos del mismo color sin que el compilador chiste.
                    owner={integrantes.find((m) => m.id === goal.ownerMemberId) ?? null}
                  />
                ))}
              </div>
            </section>
          </>
        )}

        <AccesoRevisiones />
      </div>
    </>
  )
}

/**
 * Entrada a las revisiones conjuntas (§11).
 *
 * Va al final y con su propia consulta, fuera del bloque que espera a las
 * demás: es una función secundaria y no debería retrasar la pantalla del
 * ahorro. Al ir la última, aparecer tarde no empuja nada de lo que ya se está
 * leyendo.
 *
 * En un hogar de una sola persona no se dibuja: revisarse uno mismo no
 * demuestra nada y el servidor ni siquiera deja programarla.
 */
function AccesoRevisiones() {
  const revisiones = useRevisiones()
  const navigate = useNavigate()

  if (!revisiones.data?.available) return null

  const proxima = revisiones.data.next
  const dias = proxima ? differenceInDays(proxima.scheduledFor, revisiones.data.today) : null

  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        void navigate('/ahorro/revisiones')
      }}
      className="mb-2 flex min-h-[60px] w-full items-center gap-3 rounded-card border border-line bg-surface px-3.5 py-2.5 text-left active:scale-[0.99]"
    >
      <HandshakeIcon size={20} weight="fill" className="shrink-0 text-ink-soft" />
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold text-ink">Revisión conjunta</span>
        <span className="block text-[12px] leading-snug text-ink-soft">
          {dias === null
            ? 'Comprueben juntos que el ahorro existe de verdad.'
            : dias < 0
              ? `Se pasó hace ${-dias} ${-dias === 1 ? 'día' : 'días'}.`
              : dias === 0
                ? 'Toca hoy.'
                : `Faltan ${dias} ${dias === 1 ? 'día' : 'días'}.`}
        </span>
      </span>
      <CaretRightIcon size={16} weight="bold" className="shrink-0 text-ink-faint" />
    </button>
  )
}

/** Acceso al alta. Vive en el encabezado para que esté a la vista sin desplazar. */
function BotonNuevaMeta() {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        navigate('/ahorro/nueva')
      }}
      className="flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-pill bg-accion px-3.5 text-[13px] font-semibold text-on-accion active:scale-95"
    >
      <PlusIcon size={15} weight="bold" />
      Nueva meta
    </button>
  )
}

function MetaFila({
  goal,
  hoy,
  owner,
}: {
  goal: SavingsGoal
  hoy: PlainDate
  owner: Member | null
}) {
  const navigate = useNavigate()

  // Solo una meta se mide en porcentaje. Los demás fondos se miden por saldo,
  // aunque tengan un monto de referencia: una reserva de emergencia al 93% no
  // está «casi terminada» —no termina—, y enseñarla así invitaría a dejar de
  // aportarle justo cuando ya casi sirve para lo que se creó.
  //
  // `pct` en nulo es la señal de «este fondo no se mide en porcentaje», y de ahí
  // cuelga todo lo demás: el anillo, la cifra de la derecha y el pie.
  const objetivo = goal.targetAmountCents
  const esMeta = goal.kind === 'GOAL' && objetivo !== null
  const pct = esMeta ? percentOf(goal.currentAmountCents, objetivo) : null
  const alcanzada = pct !== null && pct >= 100
  const faltante = esMeta ? Math.max(0, objetivo - goal.currentAmountCents) : null
  const Icono = (goal.icon ? ICONOS[goal.icon] : undefined) ?? PiggyBankIcon

  // §19 — el arco va en el **pastel** de su dueño, no en el color pleno.
  //
  // Es el mismo código de lectura del mapa del inicio: pleno = disponible,
  // pastel = ahorrado. Un fondo es dinero ya guardado, así que pintarlo pleno lo
  // haría competir visualmente con lo que sí se puede gastar hoy.
  //
  // El fondo común no es de nadie y toma el índigo de marca, igual que en el
  // mapa; `--primary-soft` es fondo de etiqueta y como arco desaparecería contra
  // la pista, así que se mezcla al vuelo el mismo índigo intermedio.
  const arco = owner
    ? personPalette(owner.personColorToken).pastelVar
    : 'color-mix(in srgb, var(--primary) 45%, var(--surface))'

  // Ritmo mensual necesario, redondeado al peso hacia arriba.
  //
  // Dos motivos para redondear hacia arriba y no al más cercano: apartar de
  // menos deja la meta corta el último mes, justo cuando ya no hay margen de
  // maniobra; y «aparta $12,583.34 al mes» es una instrucción que nadie puede
  // seguir — los centavos convierten una meta en un cálculo.
  const diasRestantes = goal.targetDate ? differenceInDays(goal.targetDate, hoy) : null
  const mesesRestantes =
    diasRestantes !== null ? Math.max(1, Math.ceil(diasRestantes / 30.44)) : null
  const ritmoMensual =
    faltante !== null && mesesRestantes ? Math.ceil(faltante / mesesRestantes / 100) * 100 : null

  return (
    <Card className="flex flex-col gap-3">
      <header className="flex items-center gap-3">
        {pct === null ? (
          // Sin objetivo no hay anillo que llenar: un aro al 0% diría «no llevas
          // nada» de una reserva que puede llevar meses creciendo.
          <span className="flex size-14 shrink-0 items-center justify-center rounded-full bg-surface-sunken">
            <Icono size={18} weight="fill" className="text-ink-soft" />
          </span>
        ) : (
          <ProgressRing
            percent={pct}
            size={56}
            stroke={6}
            color={alcanzada ? 'var(--income)' : arco}
          >
            {alcanzada ? (
              <TrophyIcon size={18} weight="fill" className="text-income-text" />
            ) : goal.status === 'PAUSED' ? (
              <PauseCircleIcon size={18} weight="fill" className="text-ink-faint" />
            ) : (
              <Icono size={18} weight="fill" className="text-ink-soft" />
            )}
          </ProgressRing>
        )}

        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 truncate text-[15px] font-semibold text-ink">
            {owner ? <PersonDot token={owner.personColorToken} /> : null}
            {goal.name}
          </p>
          {/* Personal o del hogar en una etiqueta con el color de identidad de su
              dueño, no solo en texto gris: es la distinción que el cliente pidió
              y tiene que resolverse de un vistazo, sin leer la fila entera.

              Al punto va `personColorToken`, nunca `owner.id`: ambos son cadenas
              y el compilador no los distingue, pero pasar el id pintaría a todo
              el hogar del mismo color. */}
          <span
            className={cn(
              'mt-0.5 inline-flex max-w-full items-center gap-1.5 rounded-pill px-2 py-0.5 text-[11px] font-medium',
              owner
                ? cn(personPalette(owner.personColorToken).soft, personPalette(owner.personColorToken).text)
                : 'bg-surface-sunken text-ink-soft',
            )}
          >
            {owner ? (
              <>
                <PersonDot token={owner.personColorToken} />
                <span className="truncate">
                  Ahorro personal de {owner.user.displayName.split(' ')[0]}
                </span>
              </>
            ) : (
              <>
                <UsersThreeIcon size={12} weight="fill" />
                {/* Corta a propósito: con la cifra a la derecha, «Fondo
                    compartido del hogar» parte la etiqueta en dos renglones a
                    375 px y descuadra la fila entera. */}
                Fondo del hogar
              </>
            )}
          </span>
          <p data-money className="mt-0.5 text-[13px] text-ink">
            {esMeta ? (
              <>
                {formatMoney(goal.currentAmountCents, { hideZeroCents: true })}
                <span className="text-ink-faint">
                  {' '}
                  de {formatMoney(objetivo, { hideZeroCents: true })}
                </span>
              </>
            ) : (
              // Aquí la línea dice qué clase de fondo es. Repetir el saldo sería
              // decir dos veces lo mismo: ya está a la derecha.
              <span className="text-ink-faint">{ETIQUETA_TIPO[goal.kind]}</span>
            )}
          </p>
        </div>

        {/* En una meta, el número de la derecha es el avance; en un fondo que se
            mantiene, el saldo acumulado. Es lo que hay que poder leer de un
            vistazo en cada caso. */}
        <span
          data-money
          className={cn(
            'shrink-0 text-[15px] font-bold',
            alcanzada ? 'text-income-text' : 'text-ink',
          )}
        >
          {pct === null
            ? formatMoney(goal.currentAmountCents, { hideZeroCents: true })
            : `${Math.round(pct)}%`}
        </span>
      </header>

      {!esMeta ? (
        <p className="rounded-card bg-surface-sunken px-3 py-2 text-[12px] leading-snug text-ink-soft">
          {SIN_META[goal.kind]}
          {objetivo !== null ? (
            <>
              {' '}
              Te pusiste{' '}
              <strong className="text-ink" data-money>
                {formatMoney(objetivo, { hideZeroCents: true })}
              </strong>{' '}
              de referencia.
            </>
          ) : null}
        </p>
      ) : alcanzada ? (
        <p className="rounded-card bg-income-soft px-3 py-2 text-[12px] font-medium text-income-text">
          ¡Meta alcanzada!
        </p>
      ) : ritmoMensual !== null && mesesRestantes !== null ? (
        <p className="rounded-card bg-surface-sunken px-3 py-2 text-[12px] leading-snug text-ink-soft">
          Para llegar en {mesesRestantes} {mesesRestantes === 1 ? 'mes' : 'meses'}, aparta{' '}
          <strong className="text-ink" data-money>
            {formatMoney(ritmoMensual, { hideZeroCents: true })}
          </strong>{' '}
          al mes.
        </p>
      ) : (
        <p className="rounded-card bg-surface-sunken px-3 py-2 text-[12px] text-ink-soft">
          Faltan{' '}
          <strong className="text-ink" data-money>
            {formatMoney(faltante ?? 0, { hideZeroCents: true })}
          </strong>{' '}
          · sin fecha objetivo.
        </p>
      )}

      {goal.status === 'PAUSED' ? (
        <p className="text-[12px] leading-snug text-ink-faint">
          En pausa: no cuenta para el ahorro comprometido del mes.
        </p>
      ) : null}

      {goal.progress.pendingTransferCents > 0 ? (
        // Comprometido no es guardado: el disponible ya lo descuenta, pero el
        // dinero sigue en la cuenta de origen. Callarlo haría creer que la meta
        // tiene un saldo que nadie ha movido.
        <p className="text-[12px] leading-snug text-ink-faint">
          <strong className="font-semibold text-ink-soft" data-money>
            {formatMoney(goal.progress.pendingTransferCents, { hideZeroCents: true })}
          </strong>{' '}
          comprometidos que todavía no se traspasan.
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            haptic('light')
            navigate(`/ahorro/${goal.id}/aportar`)
          }}
          className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-card bg-accion text-[14px] font-semibold text-on-accion active:scale-[0.98]"
        >
          <PiggyBankIcon size={16} weight="fill" />
          Aportar
        </button>
        <button
          type="button"
          onClick={() => {
            haptic('light')
            navigate(`/ahorro/${goal.id}/editar`)
          }}
          aria-label={`Editar ${goal.name}`}
          className="flex min-h-[44px] items-center justify-center gap-1.5 rounded-card border border-line bg-surface px-4 text-[14px] font-semibold text-ink active:scale-[0.98]"
        >
          <PencilSimpleIcon size={16} weight="bold" />
          Editar
        </button>
      </div>
    </Card>
  )
}

/**
 * Esqueleto de carga.
 *
 * Cada hueco mide lo que medirá el contenido real —la línea de 15px del título
 * de la meta ocupa 22px, la de 12px ocupa 18—, así que al llegar los datos nada
 * se recorre. El subtítulo del encabezado también se rellena: si apareciera
 * después, el encabezado crecería y empujaría la pantalla entera hacia abajo.
 */
function Cargando() {
  return (
    <>
      {/* El botón también va en el esqueleto: aparecer después movería el
          encabezado justo cuando el dedo ya iba hacia él. */}
      <ScreenHeader title="Ahorro" subtitle="Calculando tu ahorro…" action={<BotonNuevaMeta />} />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        <Card className="flex flex-col gap-4">
          <div className="flex h-[22px] items-center">
            <Skeleton className="h-4 w-40" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="size-[120px] rounded-full" />
            <ul className="flex min-w-0 flex-1 flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <li key={i} className="flex h-4 items-center">
                  <Skeleton className="h-3 w-full" />
                </li>
              ))}
            </ul>
          </div>
        </Card>

        <section className="pb-2">
          <div className="mb-3 flex h-[22px] items-center">
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="flex flex-col gap-3">
                <header className="flex items-center gap-3">
                  <Skeleton className="size-14 rounded-full" />
                  <div className="min-w-0 flex-1">
                    <div className="flex h-[22px] items-center">
                      <Skeleton className="h-3.5 w-32" />
                    </div>
                    {/* La etiqueta de dueño mide 19px con su relleno de píldora. */}
                    <div className="mt-0.5 flex h-[19px] items-center">
                      <Skeleton className="h-4 w-40 rounded-pill" />
                    </div>
                    <div className="mt-0.5 flex h-[20px] items-center">
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                  <Skeleton className="h-4 w-9" />
                </header>
                <Skeleton className="h-[33px]" />
                <Skeleton className="h-[44px]" />
              </Card>
            ))}
          </div>
        </section>
      </div>
    </>
  )
}
